import SourceEditor from "@monaco-editor/react";
import { Command, Editor, Plugin, ButtonView } from "ckeditor5";
import { html } from "js-beautify";
import React, { useState } from "react";
import styled from "styled-components";

import {
  confirmableConfirm,
  ConfirmModal,
  Modal,
  SubmitButtons,
} from "@examspark/common-ui";
import {
  BannerWrapper,
  InfoBanner,
} from "@examspark/common-ui/dist/components/banners/Banner";
import {
  IconEditSourceLine,
  IconViewSourceLine,
} from "@examspark/common-ui/dist/components/icons";
import { ConfirmableProps } from "@examspark/common-ui/dist/utils/confirmable";

import DataApiEventDecorator, {
  DataApiDecorateEvent,
} from "./DataApiEventDecorator";

export const UI_BUTTON_HTML_SOURCE = "htmlSource";
export const COMMAND_HTML_SOURCE = "htmlSource";

const StyledBanner = styled(InfoBanner)`
  flex-grow: 1;

  ${BannerWrapper} {
    padding: 0;
    gap: 1rem;
    font-size: 1.1rem;
  }
`;

const HtmlSourceModal = ({
  initialValue,
  configIsReadOnly,
  editorIsReadOnly,
  ...props
}: ConfirmableProps<false | string> & {
  initialValue: string;
  configIsReadOnly: boolean;
  editorIsReadOnly: boolean;
}) => {
  const [value, setValue] = useState(initialValue);

  return (
    <ConfirmModal.Generic<false | string>
      defaultResult={false}
      title={`${configIsReadOnly ? "View" : "Edit"} Source`}
      size="large"
      fullHeight
      buttons={({ onResolve }) => (
        <>
          {!configIsReadOnly && (
            <>
              <StyledBanner>
                {editorIsReadOnly
                  ? "Editor is read-only."
                  : "Not all HTML is allowed in the rich content editor. Changes made in HTML mode may be discarded."}
              </StyledBanner>
              <SubmitButtons
                submitLabel="Save"
                submitTooltip={
                  editorIsReadOnly ? "Editor is read-only" : "Save"
                }
                isSubmitDisabled={editorIsReadOnly}
                onSubmit={() => onResolve(value)}
                onCancel={() => onResolve(false)}
              />
            </>
          )}
        </>
      )}
      contents={({ onResolve }) => (
        <Modal.PaddedBody>
          <SourceEditor
            height="100%"
            value={value}
            onChange={(e) => setValue(e ?? "")}
            language="html"
            options={{
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: "on",
              wrappingIndent: "same",
              readOnly: editorIsReadOnly || configIsReadOnly,
            }}
            onMount={(editor) =>
              editor.onKeyDown((e) => {
                if (e.code === "Enter" && e.metaKey) {
                  onResolve(editor.getValue());
                  e.stopPropagation();
                  e.preventDefault();
                }
              })
            }
          />
        </Modal.PaddedBody>
      )}
      {...props}
    />
  );
};

class HtmlSourceCommand extends Command {
  private readOnly: boolean;

  constructor(editor: Editor, readOnly: boolean) {
    super(editor);
    this._isEnabledBasedOnSelection = false;
    this.affectsData = false;
    this.readOnly = readOnly;
  }

  async execute() {
    const initialValue = html(this.editor.data.get(), {
      content_unformatted: ["pre", "math"],
    });

    const update = await confirmableConfirm(HtmlSourceModal, {
      initialValue,
      configIsReadOnly: this.readOnly,
      editorIsReadOnly: this.editor.isReadOnly,
    });
    if (!update) {
      return;
    }

    //
    // Suppress DataApiEventDecorator behavior so we still get change events
    // The decorator already doesn't decorate the event if the batchtype is
    // marked as undoable, but there is a bug in the mathtype plugin which causes
    // the isUndoable flag to get lost
    //
    // https://github.com/wiris/html-integrations/issues/885
    //
    // Note: in fixes.ts we are undoing the MathType monkey-patch, so this is
    // currently not needed. But I'm leaving it here to keep the code orthogonal
    //
    this.editor.plugins
      .get(DataApiEventDecorator)
      .once<DataApiDecorateEvent>("decorateEvent", (e) => e.stop(), {
        priority: "highest",
      });

    this.editor.data.set(update, { batchType: { isUndoable: true } });

    this.editor.focus();
  }
}

export default class HtmlSource extends Plugin {
  init() {
    const readOnly =
      this.editor.config.get("allowEditSource") !== true ||
      this.editor.isReadOnly;
    const command = new HtmlSourceCommand(this.editor, readOnly);
    this.editor.commands.add(COMMAND_HTML_SOURCE, command);

    this.editor.ui.componentFactory.add(UI_BUTTON_HTML_SOURCE, (locale) => {
      const button = new ButtonView(locale);
      button.set({
        label: this.editor.t(`${readOnly ? "View" : "Edit"} Source`),
        icon: readOnly
          ? IconViewSourceLine.Content
          : IconEditSourceLine.Content,
        tooltip: true,
      });

      button.on("execute", () => {
        command.execute();
      });

      button.bind("isEnabled").to(command, "isEnabled");

      return button;
    });
  }
}

declare module "ckeditor5" {
  interface EditorConfig {
    allowEditSource?: boolean;
  }
}
